// src/components/AsideMenu/AsideMenu.jsx
import React, { useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { MdDashboard } from "react-icons/md";
import {
  FaUsers,
  FaBars,
  FaBell,
  FaCheck,
  FaVoteYea,
  FaCalendarAlt,
  FaIdBadge,
  FaFileInvoiceDollar,
  FaCalendarCheck,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaBookReader ,
} from "react-icons/fa";
import { IoPeopleSharp } from "react-icons/io5";
import { GiOrganigram, GiGraduateCap } from "react-icons/gi";
import { MdOndemandVideo } from "react-icons/md";
import { useSidebar } from "../../contexts/SidebarContext";

const NAVBAR_OFFSET = "88px";

const MENU_SECTIONS = [
  {
    label: "Tableau de bord",
    items: [
      {
        title: "Tableau de bord",
        path: "/appli/dashboard",
        matchPaths: ["/appli/dashboard"],
        icon: <MdDashboard />,
      },
    ],
  },
  {
    label: "Administratif",
    items: [
      {
        title: "Notifications",
        path: "/appli/notifications",
        matchPaths: ["/appli/notifications"],
        icon: <FaBell />,
      },
      {
        title: "Utilisateurs",
        path: "/appli/utilisateurs",
        matchPaths: ["/appli/utilisateurs"],
        icon: <FaUsers />,
      },
      {
        title: "Badges Numériques",
        path: "/appli/badge-users",
        matchPaths: ["/appli/badge-users"],
        icon: <FaIdBadge />,
      },
      {
        title: "Ligue & Commissions",
        path: "/appli/ligue-commissions",
        matchPaths: ["/appli/ligue-commissions"],
        icon: <GiOrganigram />,
      },
      {
        title: "Formation des Dirigeants",
        path: "/appli/formation-dirigeants",
        matchPaths: ["/appli/formation-dirigeants"],
        icon: <GiGraduateCap />,
      },
      {
        title: "Clubs NA",
        path: "/appli/clubs-admin",
        matchPaths: ["/appli/clubs-admin"],
        icon: <FaUsers />,
      },
      {
        title: "Votes & Sondages",
        path: "/appli/votes-admin",
        matchPaths: ["/appli/votes-admin"],
        icon: <FaVoteYea />,
      },
    ],
  },
  {
    label: "Convocations",
    items: [
      {
        title: "Groupes",
        path: "/appli/groupes",
        matchPaths: ["/appli/groupes"],
        icon: <IoPeopleSharp />,
      },
      {
        title: "Acteurs",
        path: "/appli/acteurs",
        matchPaths: ["/appli/acteurs"],
        icon: <FaUsers />,
      },
      {
        title: "Convocations",
        path: "/appli/convocations",
        matchPaths: ["/appli/convocations", "/appli/convocations-admin"],
        icon: <FaCalendarCheck />,
      },
      {
        title: "Fiches de frais",
        path: "/appli/expense-claims",
        matchPaths: ["/appli/expense-claims"],
        icon: <FaFileInvoiceDollar />,
      },
    ],
  },
  {
    label: "Calendrier",
    items: [
      {
        title: "Calendrier",
        path: "/appli/calendrier",
        matchPaths: ["/appli/calendrier"],
        icon: <FaCalendarAlt />,
      },
    ],
  },
  {
    label: "Centre National",
    items: [
      {
        title: "Inscriptions CN",
        path: "/appli/inscriptions-cn",
        matchPaths: ["/appli/inscriptions-cn"],
        icon: <FaCheck />,
      },
      {
        title: "Licenciés inscrits",
        path: "/appli/licencies-inscrits",
        matchPaths: ["/appli/licencies-inscrits"],
        icon: <FaUsers />,
      },
    ],
  },
  {
    label: "Arbitrage",
    items: [
      {
        title: "Règlements Arbitrage",
        path: "/appli/reglements-arbitrage",
        matchPaths: ["/appli/reglements-arbitrage"],
        icon: <FaBookReader />,
      },
      {
        title: "Quizz Arbitrage",
        path: "/appli/quizz-arbitrage",
        matchPaths: ["/appli/quizz-arbitrage"],
        icon: <MdOndemandVideo />,
      },
    ],
  },
];

function isMenuActive(pathname, item) {
  return (item.matchPaths || [item.path]).some(
    (basePath) =>
      pathname === basePath ||
      pathname.startsWith(`${basePath}/`) ||
      pathname.startsWith(`${basePath}?`),
  );
}

function SectionTitle({ label, isCollapsed, mobile = false }) {
  if (isCollapsed && !mobile) {
    return (
      <div className="mx-3 my-4 flex items-center justify-center">
        <div className="h-px w-full bg-slate-200/90 dark:bg-slate-800/90" />
      </div>
    );
  }

  return (
    <div className="px-3 pb-2 pt-3">
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
        <p className="shrink-0 text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400 dark:text-slate-500">
          {label}
        </p>
        <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
      </div>
    </div>
  );
}

function SectionBlock({
  section,
  pathname,
  isCollapsed,
  onNavigate,
  mobile = false,
}) {
  return (
    <div className="mb-2">
      <SectionTitle
        label={section.label}
        isCollapsed={isCollapsed}
        mobile={mobile}
      />

      <div className="space-y-1">
        {section.items.map((item) => {
          const active = isMenuActive(pathname, item);

          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onNavigate}
              title={item.title}
              className={`group mx-2 flex items-center rounded-2xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                isCollapsed && !mobile ? "justify-center" : "gap-3"
              } ${
                active
                  ? "bg-gradient-to-r from-[#013E8E] via-indigo-600 to-[#0f5cc0] text-white shadow-md hover:text-white"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
              }`}
            >
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-base transition-all ${
                  active
                    ? "bg-white/15 text-white group-hover:bg-white/20 group-hover:text-white"
                    : "bg-slate-100 text-slate-700 group-hover:bg-white group-hover:text-slate-900 dark:bg-slate-800 dark:text-slate-200 dark:group-hover:bg-slate-700 dark:group-hover:text-white"
                }`}
              >
                {item.icon}
              </span>

              {(!isCollapsed || mobile) && (
                <span
                  className={`truncate transition-colors ${
                    active
                      ? "text-white group-hover:text-white"
                      : "text-inherit"
                  }`}
                >
                  {item.title}
                </span>
              )}
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}

export default function AsideMenu() {
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const shellWidth = useMemo(() => {
    return isCollapsed ? "w-24" : "w-72";
  }, [isCollapsed]);

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      {/* Bouton mobile */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="fixed left-4 z-[70] flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white/95 text-slate-800 shadow-lg backdrop-blur dark:border-slate-700 dark:bg-slate-900/95 dark:text-white lg:hidden"
        style={{ top: "104px" }}
        aria-label="Ouvrir le menu administrateur"
      >
        <FaBars />
      </button>

      {/* Aside desktop */}
      <aside
        className={`fixed left-0 z-40 hidden ${shellWidth} flex-col border-r border-slate-200/80 bg-white/95 backdrop-blur-xl transition-all duration-300 dark:border-slate-800/80 dark:bg-slate-900/95 lg:flex`}
        style={{
          top: NAVBAR_OFFSET,
          height: `calc(100dvh - ${NAVBAR_OFFSET})`,
        }}
      >
        <div className="flex items-center justify-end px-4 pb-3 pt-4">
          <button
            type="button"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            aria-label={isCollapsed ? "Déplier le menu" : "Replier le menu"}
          >
            {isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-1 pb-6">
          {MENU_SECTIONS.map((section) => (
            <SectionBlock
              key={section.label}
              section={section}
              pathname={location.pathname}
              isCollapsed={isCollapsed}
            />
          ))}
        </div>
      </aside>

      {/* Aside mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[80] lg:hidden">
          <div
            className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm"
            onClick={closeMobile}
          />

          <aside
            className="absolute left-0 w-[88vw] max-w-[340px] overflow-hidden border-r border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900"
            style={{
              top: NAVBAR_OFFSET,
              height: `calc(100dvh - ${NAVBAR_OFFSET})`,
            }}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-4 dark:border-slate-800">
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-white">
                  Menu administrateur
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Navigation du tableau de bord
                </p>
              </div>

              <button
                type="button"
                onClick={closeMobile}
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                aria-label="Fermer le menu"
              >
                <FaTimes />
              </button>
            </div>

            <div className="h-[calc(100%-82px)] overflow-y-auto px-2 pb-6">
              {MENU_SECTIONS.map((section) => (
                <SectionBlock
                  key={section.label}
                  section={section}
                  pathname={location.pathname}
                  isCollapsed={false}
                  onNavigate={closeMobile}
                  mobile
                />
              ))}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}